/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  alpha,
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  Stack,
  styled,
  Typography,
  useTheme
} from '@mui/material';
import { useHolidayDiscount } from 'configs/coupon';
import { ConfigContext } from 'context';
import InstavisionButton from 'coreComponents/Button/Button';
import analyticsService, {
  AppEvent
} from 'infrastructure/serviceHandlers/Analytics/Analytics';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isIOS, isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { TPlanList, TSpaceState } from 'typings';
import { formatPrice, getSupportedCameraCount } from 'utils/commonFunctions';
// import EnhancedPlanIcon from '../../assets/instaview/planLevelTwoIcon.svg';
// import PremiumPlanIcon from '../../assets/instaview/planLevelThreeIcon.svg';
// import SoloPlanIcon from '../../assets/instaview/planLevelOneIcon.svg';
import { partnerAssets } from 'partners/partnersAssets';
import { useAppSelector } from 'store/hooks';
import { RootState } from 'store/store';
import { PROSECURITY_COUNTRIES } from 'utils/constants';
import PromoRibbon from './PromoRibbon';
// eslint-disable-next-line import/order

const planIcons = [
  partnerAssets.planLevelThreeIcon,
  partnerAssets.planLevelTwoIcon,
  partnerAssets.planLevelOneIcon
];

const PlanCard = styled(Card)<{ selected: boolean; planIndex: number }>(
  ({ theme, planIndex, selected }) => ({
    overflow: planIndex === 1 ? 'visible' : 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '64px',
    height: '100%',
    // cursor: 'pointer',
    padding: 52,
    paddingBottom: 8,
    // transition: 'all 0.3s ease',
    borderRadius: 10,
    border: selected
      ? `0.5px solid ${theme.palette.primary.main}`
      : `0.5px solid ${theme.palette.text.secondary}`,
    backgroundColor: selected
      ? alpha(theme.palette.primary.main, 0.1)
      : theme.palette.background.default,
    WebkitTapHighlightColor: 'transparent'
    // '&:hover': {
    //   transform: 'translateY(-4px)',
    //   boxShadow: theme.shadows[4]
    // }
  })
);

const FeatureContainer = styled(Box)<{
  planIndex: number;
  feature: string;
  containerWidth: number | null;
  planCount: number;
}>(({ feature, planIndex, containerWidth, planCount, theme }) => {
  const isOdd = planCount % 2 !== 0;
  const middleIndex = Math.floor(planCount / 2);
  const shouldShowFeature = isOdd && planIndex === middleIndex;

  return {
    height: 40,
    width: 24,
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'center',
    position: 'relative',

    ...(shouldShowFeature && {
      '&:before': {
        content: `"${feature}"`,
        position: 'absolute',
        top: '0',
        left: '0',
        transform: 'translateX(-46.5%) translateY(-100%)',
        padding: '8px 16px',
        width: containerWidth ? 0.98 * containerWidth : 0.98 * 400,
        backgroundColor: theme.palette.background.light,
        border: `0.5px solid ${theme.palette.text.secondary}`,
        zIndex: 1,
        borderRadius: 4,
        // overflow: 'visible',
        fontSize: theme.typography.body3.fontSize,
        fontWeight: theme.typography.body3.fontWeight,
        textAlign: 'center',
        pointerEvents: 'none',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
      }
    }),
    ...(!isOdd &&
      planIndex === middleIndex && {
        '&:before': {
          content: `"${feature}"`,
          position: 'absolute',
          top: '0',
          left: '0',
          transform: 'translateX(-72.9%) translateY(-100%)',
          padding: '8px 16px',
          width: containerWidth ? 0.98 * containerWidth : 0.98 * 400,
          backgroundColor: theme.palette.background.light,
          border: `0.5px solid ${theme.palette.text.secondary}`,
          zIndex: 1,
          borderRadius: 4,
          // overflow: 'visible',
          fontSize: theme.typography.body3.fontSize,
          fontWeight: theme.typography.body3.fontWeight,
          textAlign: 'center',
          pointerEvents: 'none',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis'
        }
      })
  };
});

const MonthlyButton = styled(Button)<{ isYearly: boolean; padding?: string }>(
  ({ isYearly, theme }) => ({
    width: '50%',
    height: '100%',
    display: 'flex',
    whiteSpace: 'nowrap', // Prevent wrapping
    overflow: 'hidden', // Handle overflow
    textTransform: 'none',
    textOverflow: 'ellipsis',
    flexDirection: 'column',
    color: !isYearly
      ? theme.palette.text.contrastText
      : theme.palette.text.primary,
    // padding: padding || '12px 44px',
    paddingTop: '12px',
    paddingBottom: '12px',
    borderRadius: '10px',
    background: !isYearly
      ? theme.palette.gradient.primary
      : theme.palette.gradient.grey,
    boxShadow: !isYearly
      ? theme.palette.shadows.darkBlack
      : theme.palette.shadows.lightBlack,
    '&:focus, &:active, &:hover': {
      background: !isYearly
        ? theme.palette.gradient.primary
        : theme.palette.gradient.grey,
      boxShadow: !isYearly
        ? theme.palette.shadows.darkBlack
        : theme.palette.shadows.lightBlack
    }

    // border: !isYearly ? 'none' : '0.5px solid',
    // borderImageSource: theme.palette.borderImageSource.white,
  })
);

const YearlyButton = styled(Button)<{ isYearly: boolean; padding?: string }>(
  ({ isYearly, theme }) => ({
    width: '50%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    color: isYearly
      ? theme.palette.text.contrastText
      : theme.palette.text.primary,
    padding: '12px 4px 12px 12px',
    whiteSpace: 'nowrap', // Prevent wrapping
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    borderRadius: '10px',
    background: isYearly
      ? theme.palette.gradient.primary
      : theme.palette.gradient.grey,
    boxShadow: isYearly
      ? theme.palette.shadows.darkBlack
      : theme.palette.shadows.lightBlack,
    // border: isYearly ? 'none' : '0.5px solid',
    // borderImageSource: theme.palette.borderImageSource.white,
    textTransform: 'none',
    '&:focus, &:active, &:hover': {
      background: isYearly
        ? theme.palette.gradient.primary
        : theme.palette.gradient.grey,
      boxShadow: isYearly
        ? theme.palette.shadows.darkBlack
        : theme.palette.shadows.lightBlack
    }
  })
);

const RoundBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '50%',
  background: theme.palette.background.light,
  color: theme.palette.text.secondary,
  padding: '3px',
  fontSize: theme.typography.body2.fontSize,
  border: `2px solid ${theme.palette.primary.main}`
}));

const RoundOuterBox = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: 'auto',
  transform: 'translate(-50%, -50%)',
  borderRadius: '50%',
  background: theme.palette.primary.light,
  color: theme.palette.text.secondary,
  fontSize: theme.typography.body2.fontSize,
  border: `2px solid ${theme.palette.primary.light}`
}));

const PriceBox = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.title2.fontSize,
  fontWeight: 600
}));

type LocalizedFeatureDescriptions = Record<
  string,
  {
    default: string;
    [key: string]: string; // for other keys like contextualAI1, contextualAI2, etc.
  }
>;

type TProps__PlansComparision = {
  plansData: TPlanList[];
  allPlans: TPlanList[];
  allowSelection?: boolean;
  onPlanSelection?: (plan: TPlanList) => void;
  isSelectedPlanEligibleForUpgrade?: (plan: TPlanList) => number | boolean;
  isFreeTrial?: boolean;
  isUpgrade?: boolean;
  onChange: (arg0: boolean, planList: TPlanList[]) => void;
};

function PlanComparisonComponent(props: TProps__PlansComparision) {
  const {
    plansData,
    allowSelection = false,
    onPlanSelection,
    isSelectedPlanEligibleForUpgrade,
    isFreeTrial,
    isUpgrade,
    onChange,
    allPlans
  } = props;

  const appTheme = useTheme();
  const location = useLocation();

  const isBirdCamera = location.pathname.includes('bird');
  // console.log('is free trial', isFreeTrial);

  const planContainerRef = useRef<HTMLDivElement>(null);
  const config = useContext(ConfigContext);

  const [isYearly, setIsYearly] = useState(true);
  const [, setSearchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState<TPlanList>();
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'pages.subscription.planPage'
  });

  const currentLocale = i18n.language;

  console.log('currentLocale', currentLocale);

  const localizedDescriptions: LocalizedFeatureDescriptions = {
    '30_days_event': {
      default: t('feature.eventStorage')
    },
    contextual_ai: {
      default: t('feature.contextualAI'),
      contextualAI1: t('feature.contextualAI1'),
      contextualAI2: t('feature.contextualAI2'),
      contextualAI3: t('feature.contextualAI3') // Renamed to fix duplicate key
    },
    // cameras_supported: {
    //   default: t('feature.camerasSupported')
    // },
    monitoring: {
      default: t('feature.monitoring')
    },
    monitoring_cert: {
      default: t('feature.monitoringCert')
    }
  };

  const { IS_HOLIDAY_DISCOUNT_ACTIVE, HOLIDAY_DISCOUNT_PERCENTAGE } =
    useHolidayDiscount();

  const [planContainerWidth, setPlanContainerWidth] = useState<number | null>(
    null
  );

  const [
    isCancellationAndRefundPolicyAccepted,
    setIsCancellationAndRefundPolicyAccepted
  ] = useState(true);

  const enableHolidayPromo = IS_HOLIDAY_DISCOUNT_ACTIVE && !isUpgrade;

  useEffect(() => {
    if (planContainerRef.current) {
      setPlanContainerWidth(planContainerRef.current.clientWidth);
    }
  }, [plansData]);

  useEffect(() => {
    if (plansData.length > 0) {
      const highestLevelPlan = plansData.reduce((prev, curr) => {
        return curr.level > prev.level ? curr : prev;
      });
      setSelectedPlan(highestLevelPlan);
    }
  }, [plansData]);

  // const handleRadioChange = (plan: TPlanList, selectedIndex: number) => {
  //   if (filterByLevel && !filterByLevel(plan)) {
  //     return;
  //   }
  //   console.log('clicked radio', plan, selectedIndex);
  //   setSelectedPlan(plan);
  // };

  const handleRadioChange = (plan: TPlanList, selectedIndex: number) => {
    if (
      isSelectedPlanEligibleForUpgrade &&
      !isSelectedPlanEligibleForUpgrade(plan)
    ) {
      return;
    }

    console.log('clicked radio', plan, selectedIndex);
    setSelectedPlan(plan);

    // Map plan levels to their corresponding names
    const planName =
      plan.level === 1
        ? 'solo'
        : plan.level === 2
        ? 'enhanced'
        : plan.level === 3
        ? 'premium'
        : 'solo'; // Default case for safety

    // Track the event with Amplitude
    analyticsService.trackEvent(
      isYearly
        ? AppEvent.SELECTION_YEARLY_BILLING
        : AppEvent.SELECTION_MONTHLY_BILLING,
      {
        plan_level: plan.level, // Add the plan level as a property
        plan_name: planName, // Add the plan name as a property,
        ...(isBirdCamera && { 'bird-user': true })
      }
    );

    console.log(`Selected plan: ${planName}`);
  };

  const checkUpgradeEligibility = useMemo(() => {
    // check if none of the plans are available for upgrade
    return plansData.every(
      (plan) =>
        isSelectedPlanEligibleForUpgrade &&
        !isSelectedPlanEligibleForUpgrade(plan)
    );
  }, [isSelectedPlanEligibleForUpgrade, plansData]);

  // const uniqueFeatures = Array.from(
  //   new Set(plansData.flatMap((plan) => plan.description))
  // );

  const featureMap = new Map();

  plansData.forEach((plan) => {
    plan.supported_features.forEach((feature) => {
      if (!featureMap.has(feature.id)) {
        featureMap.set(feature.id, feature);
      }
    });
  });

  const planFeatures = Array.from(featureMap.values()).sort(
    (a, b) => a.order - b.order
  );

  console.log('planFeatures', planFeatures);

  const handleToggleYearlyMonthly = (yearly: boolean) => {
    setIsYearly(yearly);
    onChange(yearly, allPlans);
  };

  const getCorrespondingYearlyPlan = useMemo(() => {
    if (selectedPlan) {
      const yearlyPlan = allPlans.find(
        (plan) =>
          plan.level === selectedPlan.level &&
          plan.name === selectedPlan.name &&
          plan.subscription_cycle === 12
      );
      return yearlyPlan;
    }
    return null;
  }, [selectedPlan, allPlans]);

  const getCorrespondingMonthlyPlan = (plan?: TPlanList) => {
    if (plan) {
      const monthlyPlan = allPlans.find(
        (eachPlan) =>
          eachPlan.level === plan.level &&
          eachPlan.name === plan.name &&
          eachPlan.subscription_cycle === 1
      );
      return monthlyPlan;
    }
    return null;
  };

  const calculatePercentageOff = (
    originalPrice: number,
    currentPrice: number
  ): number => {
    if (originalPrice <= 0) return 0; // Prevent division by zero or invalid input
    return ((originalPrice - currentPrice) / originalPrice) * 100;
  };

  const { spaceData } = useAppSelector(
    (state: RootState) => state.space as TSpaceState
  );

  // const getCorrespondingPromoRibbonLabel = (
  //   plan: TPlanList,
  //   holidayDiscountPercentage: number
  // ) => {
  //   if (isYearly) {
  //     switch (plan.level) {
  //       case 1:
  //         return enableHolidayPromo
  //           ? `${calculatePercentageOff(
  //               plan?.prices?.[0]?.original_price ?? 0,
  //               (plan?.prices?.[0]?.price ?? 0) *
  //                 (1 - holidayDiscountPercentage / 100)
  //             ).toFixed(0)}% off`
  //           : `${calculatePercentageOff(
  //               plan?.prices?.[0]?.original_price ?? 0,
  //               plan?.prices?.[0]?.price ?? 0
  //             ).toFixed(0)}% off`;
  //       case 2:
  //         return enableHolidayPromo
  //           ? `${calculatePercentageOff(
  //               plan?.prices?.[0]?.original_price ?? 0,
  //               (plan?.prices?.[0]?.price ?? 0) *
  //                 (1 - holidayDiscountPercentage / 100)
  //             ).toFixed(0)}% off`
  //           : `${calculatePercentageOff(
  //               plan?.prices?.[0]?.original_price ?? 0,
  //               plan?.prices?.[0]?.price ?? 0
  //             ).toFixed(0)}% off`;
  //       case 3:
  //         return enableHolidayPromo
  //           ? `${calculatePercentageOff(
  //               plan?.prices?.[0]?.original_price ?? 0,
  //               (plan?.prices?.[0]?.price ?? 0) *
  //                 (1 - holidayDiscountPercentage / 100)
  //             ).toFixed(0)}% off`
  //           : `${calculatePercentageOff(
  //               plan?.prices?.[0]?.original_price ?? 0,
  //               plan?.prices?.[0]?.price ?? 0
  //             ).toFixed(0)}% off`;
  //       default:
  //         return '';
  //     }
  //   }
  //   //  else if (!isYearly && plan.level === 3) {
  //   //   return 'PRIME';
  //   // }
  //   return '';
  // };

  const getLocalizedFeatureLabels = (
    featureId: string,
    country: string
  ): string[] => {
    const descObj = localizedDescriptions[featureId];
    if (!descObj) return [];

    const normalizedCountry = country?.toUpperCase();
    const isProSecurity = PROSECURITY_COUNTRIES.includes(normalizedCountry);

    if (!isProSecurity && featureId === 'contextual_ai') {
      const contextualLabels = Object.entries(descObj)
        .filter(([key]) => key.startsWith('contextualAI'))
        .map(([, val]) => val);

      // fallback to default if contextual labels are empty (safety)
      return contextualLabels.length > 0 ? contextualLabels : [descObj.default];
    }

    // fallback to default for PROSECURITY_COUNTRIES or non-contextual_ai features
    return descObj.default ? [descObj.default] : [];
  };
  const getCorrespondingPromoRibbonLabel = (
    plan: TPlanList,
    holidayDiscountPercentage: number
  ) => {
    if (!isYearly) return '';

    const originalPrice = plan?.prices?.[0]?.original_price ?? 0;
    const currentPrice = plan?.prices?.[0]?.price ?? 0;

    const discountedPrice = enableHolidayPromo
      ? currentPrice * (1 - holidayDiscountPercentage / 100)
      : currentPrice;

    const discount = calculatePercentageOff(originalPrice, discountedPrice);
    const roundedDiscount = Math.round(discount);

    return roundedDiscount > 0 ? `${roundedDiscount}% off` : '';
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        gap={1}
        my={3}
        position="relative"
        ref={planContainerRef}
        width="100%"
      >
        <MonthlyButton
          variant="contained"
          color="inherit"
          isYearly={isYearly}
          onClick={() => handleToggleYearlyMonthly(false)}
        >
          <Box>
            <PriceBox sx={{ fontSize: '0.82rem' }}>
              {formatPrice(
                getCorrespondingMonthlyPlan(selectedPlan)?.prices?.[0]?.price,
                getCorrespondingMonthlyPlan(selectedPlan)?.prices?.[0]
                  ?.currency,
                getCorrespondingMonthlyPlan(selectedPlan)?.prices?.[0]?.country,
                currentLocale
              )}
              {t('perMonth')}
            </PriceBox>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.78rem' }}>
              {t('billedMonthly')}
            </Typography>
          </Box>
        </MonthlyButton>
        <YearlyButton
          isYearly={isYearly}
          variant="contained"
          color="inherit"
          sx={{}}
          onClick={() => {
            handleToggleYearlyMonthly(true);
          }}
          onTouchStart={() => handleToggleYearlyMonthly(true)} // Safari iOS compatibility
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5
            }}
          >
            <PriceBox sx={{ fontSize: '0.82rem' }}>
              {formatPrice(
                (enableHolidayPromo
                  ? (getCorrespondingYearlyPlan?.prices?.[0]?.price || 0) *
                    (1 - HOLIDAY_DISCOUNT_PERCENTAGE / 100)
                  : getCorrespondingYearlyPlan?.prices?.[0]?.price || 0) / 12,
                getCorrespondingYearlyPlan?.prices?.[0]?.currency,
                getCorrespondingYearlyPlan?.prices?.[0]?.country,
                currentLocale
              )}{' '}
              {t('perMonth')}
            </PriceBox>
            {getCorrespondingYearlyPlan?.prices?.[0]?.original_price !==
              getCorrespondingYearlyPlan?.prices?.[0]?.price && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '5px',
                  padding: '2px 2px',
                  height: '20px',
                  background: (theme) => theme.palette.background.light,
                  boxShadow: (theme) => theme.palette.shadows.lightBlack
                }}
              >
                <p
                  style={{
                    textTransform: 'uppercase',
                    fontSize: '8px',
                    margin: 0,
                    color: 'transparent',
                    background: appTheme.palette.gradient.primary,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text'
                  }}
                >
                  {t('save')}{' '}
                  {enableHolidayPromo
                    ? `${calculatePercentageOff(
                        getCorrespondingYearlyPlan?.prices?.[0]
                          ?.original_price ?? 0,
                        (getCorrespondingYearlyPlan?.prices?.[0]?.price ?? 0) *
                          (1 - HOLIDAY_DISCOUNT_PERCENTAGE / 100)
                      ).toFixed(0)}%`
                    : `${calculatePercentageOff(
                        getCorrespondingYearlyPlan?.prices?.[0]
                          ?.original_price ?? 0,
                        getCorrespondingYearlyPlan?.prices?.[0]?.price ?? 0
                      ).toFixed(0)}%`}
                </p>
                <p
                  style={{
                    fontSize: '8px',
                    margin: 0
                  }}
                >
                  🔥
                </p>
              </Box>
            )}
          </Box>
          <Box>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.78rem' }}
              color={(theme) =>
                isYearly
                  ? theme.palette.text.contrastText
                  : theme.palette.text.primary
              }
            >
              {formatPrice(
                enableHolidayPromo
                  ? (getCorrespondingYearlyPlan?.prices?.[0].price || 0) *
                      (1 - HOLIDAY_DISCOUNT_PERCENTAGE / 100)
                  : getCorrespondingYearlyPlan?.prices?.[0].price,
                getCorrespondingYearlyPlan?.prices?.[0]?.currency,
                getCorrespondingYearlyPlan?.prices?.[0]?.country,
                currentLocale
              )}{' '}
              {t('billedYearly')}
            </Typography>
          </Box>
        </YearlyButton>
        <RoundOuterBox>
          <RoundBox>OR</RoundBox>
        </RoundOuterBox>
      </Stack>
      <Grid container spacing={1.5}>
        {plansData.map((plan, planIndex) => (
          <Grid item xs={12 / plansData.length} key={plan.id}>
            <PlanCard
              selected={selectedPlan?.id === plan.id}
              planIndex={planIndex}
              // onPointerDown={() => handleRadioChange(plan, index)} // Additional fallback for pointer events
            >
              <Box
                tabIndex={planIndex}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '4px',
                  width: '-webkit-fill-available',
                  WebkitTapHighlightColor: 'transparent',
                  WebkitUserSelect: 'none',
                  touchAction: 'manipulation',
                  cursor: 'default'
                }}
                onClick={() => {
                  handleRadioChange(plan, planIndex);
                }} // Main click handler
                onTouchStart={() => handleRadioChange(plan, planIndex)} // Safari iOS compatibility
              >
                {getCorrespondingPromoRibbonLabel(
                  plan,
                  HOLIDAY_DISCOUNT_PERCENTAGE
                ) && (
                  <PromoRibbon
                    label={getCorrespondingPromoRibbonLabel(
                      plan,
                      HOLIDAY_DISCOUNT_PERCENTAGE
                    )}
                    styles={{
                      position: 'absolute',
                      top: 0,
                      left: 0
                    }}
                  />
                )}

                <Box
                  sx={{
                    position: 'absolute',
                    top: 5,
                    right: 1,
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  <Radio
                    value={plan.id}
                    checked={selectedPlan?.id === plan.id}
                    disabled={
                      isSelectedPlanEligibleForUpgrade &&
                      !isSelectedPlanEligibleForUpgrade(plan)
                    }
                    disableRipple
                    onChange={(e) => {
                      e.stopPropagation();
                      handleRadioChange(plan, planIndex);
                    }}
                    sx={(theme) => ({
                      background: 'transparent',
                      transform: 'scale(1.1)',
                      '& .MuiSvgIcon-root': {
                        boxShadow: theme.palette.shadows.lightBlack,
                        borderRadius: '50%',
                        ...// If not disabled, make it darker
                        (!(
                          isSelectedPlanEligibleForUpgrade &&
                          !isSelectedPlanEligibleForUpgrade(plan)
                        ) && {
                          color: theme.palette.text.secondary
                        }),
                        ...// If disabled, maintain the current shade
                        (isSelectedPlanEligibleForUpgrade &&
                          !isSelectedPlanEligibleForUpgrade(plan) && {
                            boxShadow: theme.palette.shadows.lightBlack
                          })
                      },
                      '&.Mui-checked .MuiSvgIcon-root': {
                        boxShadow: theme.palette.shadows.darkBlack,
                        color:
                          (isSelectedPlanEligibleForUpgrade &&
                            isSelectedPlanEligibleForUpgrade(plan)) ||
                          !isUpgrade
                            ? theme.palette.primary.main
                            : theme.palette.shadows.lightBlack
                      },
                      WebkitTapHighlightColor: 'transparent',
                      display: 'inline-block'
                    })}
                    data-testid={`plan-radio-${planIndex}`}
                  />
                </Box>

                <Box
                  sx={{
                    maxWidth: '30px',
                    height: '24px',
                    width: '24px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  <img
                    src={planIcons[planIndex]}
                    alt="Plan Icon"
                    style={{ width: '100%', height: 'auto' }}
                  />
                </Box>
                <Typography variant="title2" gutterBottom>
                  {plan?.name?.replace('Security', '')}
                </Typography>
                <Typography variant="body1" component="div" gutterBottom>
                  {/* For yearly plan we are applying discount on price for promo */}
                  {isYearly
                    ? enableHolidayPromo
                      ? `${formatPrice(
                          ((plan?.prices[0].price || 0) / 12) *
                            (1 - HOLIDAY_DISCOUNT_PERCENTAGE / 100),
                          plan?.prices[0].currency,
                          plan?.prices[0].country,
                          currentLocale
                        )}${t('perMonth')}`
                      : `${formatPrice(
                          (plan?.prices[0].price || 0) / 12,
                          plan?.prices[0].currency,
                          plan?.prices[0].country,
                          currentLocale
                        )}${t('perMonth')}`
                    : `${formatPrice(
                        plan?.prices[0].price || 0,
                        plan?.prices[0].currency,
                        plan?.prices[0].country,
                        currentLocale
                      )}`}
                </Typography>
                <Typography
                  textAlign="center"
                  variant="body2"
                  gutterBottom={false}
                >
                  {/* For yearly plan we are giving discount coupon promo */}

                  {isYearly
                    ? enableHolidayPromo
                      ? `${formatPrice(
                          (plan?.prices[0].price || 0) *
                            (1 - HOLIDAY_DISCOUNT_PERCENTAGE / 100),
                          plan?.prices[0].currency,
                          plan?.prices[0].country,
                          currentLocale
                        )} ${t('billedYearly')}`
                      : `${formatPrice(
                          plan?.prices[0].price || 0,
                          plan?.prices[0].currency,
                          plan?.prices[0].country,
                          currentLocale
                        )} ${t('billedYearly')}`
                    : `${t('billedMonthly')}`}
                </Typography>
              </Box>
              {/* Feature Icons */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 5,
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                <FeatureContainer
                  planIndex={planIndex}
                  feature={t('feature.camerasSupported')}
                  containerWidth={planContainerWidth}
                  planCount={plansData?.length}
                >
                  {getSupportedCameraCount(plan.supported_features)}
                </FeatureContainer>
                {planFeatures.map((feature) => {
                  const featureId = feature.id;
                  const labels = getLocalizedFeatureLabels(
                    featureId,
                    spaceData?.address?.country || 'US'
                  );

                  return labels.map((label, index) => (
                    <FeatureContainer
                      key={`${featureId}-${index}`}
                      planIndex={planIndex}
                      feature={label}
                      containerWidth={planContainerWidth}
                      planCount={plansData?.length}
                    >
                      {plan.supported_features.some(
                        (f) => f.id === featureId
                      ) ? (
                        <CheckIcon
                          sx={{
                            color: (theme) => theme.palette.tertiary.main
                          }}
                        />
                      ) : (
                        <CloseIcon
                          sx={{
                            color: (theme) => theme.palette.secondary.main
                          }}
                        />
                      )}
                    </FeatureContainer>
                  ));
                })}
              </Box>
            </PlanCard>
          </Grid>
        ))}
      </Grid>
      {allowSelection && (
        <Box>
          <Box
            sx={{
              textAlign: 'center',
              margin: 0
            }}
          >
            <FormControlLabel
              sx={{
                textDecoration: 'underline',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                marginTop: 1,
                marginBottom: 1,
                textAlign: 'left'
              }}
              disabled={!selectedPlan || checkUpgradeEligibility}
              control={
                <Checkbox
                  checked={isCancellationAndRefundPolicyAccepted}
                  onChange={(e) => {
                    setIsCancellationAndRefundPolicyAccepted(e.target.checked);
                    setSearchParams(
                      (prev) => {
                        prev.set(
                          'acceptedCancellationAndRefundPolicy',
                          String(e.target.checked)
                        );
                        return prev;
                      },
                      {
                        state: {
                          ...location.state
                        }
                      }
                    );
                  }}
                  sx={{
                    color: 'text.secondary',
                    '&.Mui-checked': {
                      color:
                        !selectedPlan || checkUpgradeEligibility
                          ? 'text.secondary'
                          : 'primary.main'
                    },
                    margin: 0
                  }}
                  size="medium"
                />
              }
              label={
                <Link
                  to="/cancellation-refund-policy"
                  state={{
                    from: location.pathname,
                    ...location.state
                  }}
                  style={{
                    color: appTheme.palette.primary.main,
                    fontSize: appTheme.typography.body2.fontSize,
                    margin: 0
                  }}
                >
                  {t('agreeToRefundAndCancellationPolicy', {
                    companyName: config.nameOfCompany
                  })}
                </Link>
              }
            />
          </Box>
          <InstavisionButton
            onClick={() => {
              if (!isMobile || (isMobile && !isIOS)) {
                if (onPlanSelection && selectedPlan) {
                  onPlanSelection(selectedPlan);
                }
              }
            }}
            onTouchStart={() => {
              if (isMobile && isIOS) {
                if (onPlanSelection && selectedPlan) {
                  onPlanSelection(selectedPlan);
                }
              }
            }}
            disabled={
              !selectedPlan ||
              checkUpgradeEligibility ||
              !isCancellationAndRefundPolicyAccepted
            }
            sx={{
              margin: '0',
              marginBottom: '20px',
              width: '100%',
              transition: 'box-shadow 0.1s ease',
              textTransform: 'capitalize'
            }}
            disableRipple
          >
            {!isUpgrade && isFreeTrial && <>{t('tryForFree')}</>}
            {!isUpgrade && !isFreeTrial && <>{t('continue')}</>}
            {isUpgrade && <>{t('continueToUpgrade')}</>}
          </InstavisionButton>
        </Box>
      )}

      {/* <InformationContainer>
        {isFreeTrial && (
          <InformationText>{t('freeCancellation')}</InformationText>
        )}
        <InformationText>{t('turnOffAutoRenewal')}</InformationText>
      </InformationContainer> */}
    </Box>
  );
}

export default PlanComparisonComponent;
